import {
  Component,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { GoogleMapsService } from '@core/services/core/google-maps.service';
import { LogService } from '@core/services/core/log.service';
import { GeocoderHelperService } from '@core/services/helpers/geocoder-helper.service';
import { Geoposition } from '@ionic-native/geolocation/ngx';
import { GoogleMapResult, GoogleMapsCoords, LatLng } from '@models/google-location';
import { spinner } from '@utils/rxjs';
import { Subscription } from 'rxjs';
import { MapContextService } from '../../../../core/services/context/map-context.service';
import { MapHelperService } from '../../../../core/services/helpers/map-helper.service';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(GoogleMap, { static: false }) gMap: GoogleMap;
  @Input() services = [];
  @Input() serviceType = '';
  @Input() uniqueMarker = true;
  @Input() searchedLocation: LatLng;
  @Input() isRoadAssistTracing: boolean;
  @Output() actualLocation = new EventEmitter();
  @Output() tilesloaded = new EventEmitter();
  @Output() infoWindowEntryClick = new EventEmitter<{
    service: any;
    entryId: string;
  }>();
  hasLoaded: boolean;
  options: google.maps.MapOptions = {};
  optionsMarker: google.maps.MarkerOptions = {};
  marker = { lat: GoogleMapsCoords.COORDS_X, lng: GoogleMapsCoords.COORDS_Y };
  multipleMarkers: any = [];
  zoom = 15;
  isInfoWindowOpen = false;
  controlsLoaded = false;
  currentPositionPWASubscription!: Subscription;
  mapClickListener: any;
  directionsDisplay: any;
  directionsService: any;
  private mapStyles = [];
  private lastOpenIndex: number = -1;

  constructor(
    private geoHelperService: GeocoderHelperService,
    private mapService: MapContextService,
    private mapHelper: MapHelperService,
    private googleMapsService: GoogleMapsService,
    private log: LogService
  ) {}

  ngOnInit() {
    this.googleMapsService.ready().then(() => {
      this.hasLoaded = true;
      this.zoom = this.uniqueMarker ? 13 : 11;
      this.mapStyles = this.mapHelper.getMapStyles();
      this.options.styles = this.mapStyles;
      this.options.disableDefaultUI = true;
      this.optionsMarker.draggable = true;
      if (this.isRoadAssistTracing) {
        this.directionsService = new google.maps.DirectionsService();
        this.directionsDisplay = new google.maps.DirectionsRenderer({
          suppressMarkers: true,
          polylineOptions: { strokeColor: "#D81E05", strokeWeight: 6 }
        });
        this.calculateAndDisplayRoute();
        this.multipleMarkers = this.mapService.setMarkers(this.services);
      } else {
        if (this.searchedLocation === undefined) {
          this.getLocation();
        }
      }
    });
  }

  ngOnChanges() {
    if (this.searchedLocation !== undefined) {
      this.onSuccess(this.searchedLocation);
    }
  }

  ngOnDestroy() {
    if (this.currentPositionPWASubscription !== undefined) {
      this.currentPositionPWASubscription.unsubscribe();
    }
    if (this.mapClickListener) {
      this.mapClickListener.remove();
    }
  }

  onMapReady() {
    if (!this.controlsLoaded) {
      this.gMap.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
        document.getElementById('gps')
      );
    }
    this.controlsLoaded = true;
    this.tilesloaded.emit();
  }

  onMapClick(event: google.maps.MapMouseEvent) {
    this.moveMarkerToPosition(event.latLng.lat(), event.latLng.lng());
  }

  async getLocation() {
    this.currentPositionPWASubscription = this.geoHelperService
      .getCurrentPosition()
      .pipe(spinner('get-location'))
      .subscribe(
        (position: Geoposition) => {
          return this.onSuccess({
            lat: +position.coords.latitude,
            lng: +position.coords.longitude
          });
        },
        err => {
          this.onError(err.code);
        }
      );
  }

  onMarkerDragEnd(e: any) {
    this.moveMarkerToPosition(e.latLng.lat(), e.latLng.lng());
  }

  onMarkerClicked(marker: any, index: number) {
    marker['isOpen'] = true;
    if (this.lastOpenIndex > -1) {
      this.multipleMarkers[this.lastOpenIndex]['isOpen'] = false;
    }
    this.lastOpenIndex = index;
  }

  onInfoWidowEntryClick(service, entryId: string) {
    this.infoWindowEntryClick.emit({ service, entryId });
  }

  private calculateAndDisplayRoute() {
    this.directionsService.route(
      {
        origin: this.services[1].street,
        destination: this.services[0].street,
        travelMode: google.maps.TravelMode.DRIVING
      },
      (response, status) => {
        if (status === "OK") {
          this.directionsDisplay.setDirections(response);
          this.directionsDisplay.setMap(this.gMap.googleMap);
        } else {
          this.log.e(this, 'Directions request failed due to ' + status);
        }
      }
    );
  }

  private async onSuccess(position: LatLng) {
    this.geoHelperService.geocodeLatLng(position).subscribe(
      (results: GoogleMapResult[]) => {
        this.actualLocation.emit(results);
        this.marker = position;
        this.searchedLocation = position;
        this.mapService.location = position;
        if (!this.uniqueMarker) {
          this.setServiceMarkers();
        }
      },
      () => {
        this.onError();
      }
    );
  }


  private moveMarkerToPosition(lat: number, lng: number) {
    this.marker = { lat, lng };
    this.onSuccess(this.marker);
  }

  private setServiceMarkers() {
    this.googleMapsService.ready().then(() => {
      this.mapService.previousMap = this.gMap;
      this.multipleMarkers = [];
      if (this.gMap) {
        this.multipleMarkers = this.mapService.setMarkers(this.services);
      }
    })
  }

  private async onError(geolocationError?: number) {
    this.actualLocation.emit({
      title: 'error',
      type: geolocationError ? geolocationError : 0
    });
  }

}
